"use client"

import React from "react"
import { sendGTMEvent } from "@next/third-parties/google"

import Controls from "../Controls/Controls"
import { useUserContext } from "src/contexts/user"
import { useWalletAuth } from "src/hooks/useWalletAuth"
import { Button, buttonMessages } from "components/inputs"
import TabbedBetslip from "compositions/TabbedBetslip/TabbedBetslip"
import LiveStatistics from "compositions/LiveStatistics/LiveStatistics"
import { ToastContainer } from "@/components/feedback/Toast/ToastContainer"
import { useDebounce, useIsMounted } from "src/hooks"

const RightSidebar: React.FC = () => {
  const { isReconnecting, isConnecting, connectOrCreateWallet } =
    useWalletAuth()
  const { isLoginComplete, isUserConnecting } = useUserContext()
  const debouncedLoginComplete = useDebounce(isLoginComplete, 500)
  const isMounted = useIsMounted()

  // Show skeleton until component is mounted to prevent hydration mismatch
  if (!isMounted) {
    return (
      <div className="animate-pulse">
        {/* Header skeleton */}
        <div className="px-3 py-3 sticky top-0 z-20 bg-accent-grey-980">
          <div className="ml-auto">
            <div className="h-10 w-32 bg-accent-grey-700 rounded" />
          </div>
        </div>
        {/* Content skeleton */}
        <div className="bg-accent-grey-900 border border-accent-grey-950 rounded-r-md -ml-px overflow-auto wd:h-fit no-scrollbar px-2 py-2">
          <div className="h-16 bg-accent-grey-700 rounded mb-2" />
          <div className="h-64 bg-accent-grey-700 rounded" />
        </div>
        {/* Bottom skeleton */}
        <div className="px-2.5 py-3 sticky bottom-5 z-20 mb:hidden">
          <div className="h-8 bg-accent-grey-700 rounded" />
        </div>
      </div>
    )
  }

  // Determine if we should show the logged-in state
  const shouldShowControls = Boolean(debouncedLoginComplete && !isConnecting)
  const isLoading = isConnecting || isReconnecting || isUserConnecting

  return (
    <>
      <div className="px-3 py-3 sticky top-0 z-20 bg-accent-grey-980">
        <div className="ml-auto flex items-center justify-end">
          {shouldShowControls ? (
            <div className="transition-opacity duration-200">
              <Controls />
            </div>
          ) : (
            <div className="transition-opacity duration-200">
              <Button
                className="ml-auto"
                title={buttonMessages.connectWallet}
                size={40}
                loading={isLoading}
                onClick={() => {
                  connectOrCreateWallet()
                  sendGTMEvent({
                    event: "wallet_connect_initiated",
                    is_betslip: false,
                    section: "header",
                    login_toolkit: "privy",
                  })
                }}
              />
            </div>
          )}
        </div>
      </div>
      <div className="bg-accent-grey-900 border border-accent-grey-950 rounded-r-md -ml-px overflow-auto wd:h-fit no-scrollbar px-2 py-2 ">
        <LiveStatistics />
        <TabbedBetslip />
      </div>
      <div className="px-2.5 py-3 sticky bottom-5 z-20 mb:hidden">
        <ToastContainer className="animate-toast-slide-in my-1" />
      </div>
    </>
  )
}

export default RightSidebar
